import * as React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, Alert, Box } from "@mui/material";
import { Localized } from "@fluent/react";
import { StructureTreeSelect } from "../../structure/StructureTreeSelect";
import { AppId } from "../../../app/appTypes";
import { useAppDispatch } from "../../../app/hooks";
import { CheckSerialNoResponse, checkSerialNo, ComputerizedSystem, updateComputerizedSystemBasicInfo } from "../computerizedSystemsApi";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import { GampCategory, toGampCategory } from "../gampDictionary";
import { GampCategorySelect } from "../components/GampCategorySelect";
import { AppRequiredMessage } from "../../../app/AppRequiredMessage";
import { StatusLevelAlert } from "../../../app/components/StatusLevelAlert";

export const EditComputerizedSystemBasicInfoDialog = ({
    computerizedSystem,
    onCancel,
    onUpdate,
}: {
    computerizedSystem: ComputerizedSystem;
    onCancel: () => void;
    onUpdate: (updated: ComputerizedSystem) => void;
}) => {
    const dispatch = useAppDispatch();
    const [name, setName] = React.useState(computerizedSystem.name);
    const [identifier, setIdentifier] = React.useState(computerizedSystem.computerizedSystemIdentifier ?? "");
    const [description, setDescription] = React.useState(computerizedSystem.description);
    const [structureId, setStructureId] = React.useState<AppId>(computerizedSystem.structureId);
    const [saving, setSaving] = React.useState(false);
    const [serverError, setServerError] = React.useState<string | null>(null);
    const [gampCategory, setGampCategory] = React.useState<GampCategory | "">(
        toGampCategory(computerizedSystem.gampCategory) ?? ""
    );

    const [validationErrors, setValidationErrors] = React.useState(false);
    const [identifierValidation, setIdentifierValidation] = React.useState<CheckSerialNoResponse | null>(null);
    const [identifierValidationBusy, setIdentifierValidationBusy] = React.useState(false);
    const identifierValidationRequestRef = React.useRef(0);
    const identifierValidationHideBusyTimeoutRef = React.useRef<number | null>(null);

    const nameErr = validationErrors && name.trim() === "";
    const identifierErr = validationErrors && identifier.trim() === "";
    const structureErr = validationErrors && !structureId;
    const gampError = validationErrors && !gampCategory;

    React.useEffect(() => {
        if (identifierValidationHideBusyTimeoutRef.current !== null) {
            window.clearTimeout(identifierValidationHideBusyTimeoutRef.current);
            identifierValidationHideBusyTimeoutRef.current = null;
        }

        const normalizedIdentifier = identifier.trim();
        const normalizedSupplierId = Number(computerizedSystem.supplierId);
        const normalizedStructureId = Number(structureId);

        if (!normalizedIdentifier || !Number.isInteger(normalizedStructureId) || normalizedStructureId <= 0 || !Number.isInteger(normalizedSupplierId) || normalizedSupplierId <= 0) {
            setIdentifierValidation(null);
            setIdentifierValidationBusy(false);
            return;
        }

        const requestId = identifierValidationRequestRef.current + 1;
        identifierValidationRequestRef.current = requestId;

        const timeoutId = window.setTimeout(async () => {
            const busyShownAt = Date.now();
            setIdentifierValidationBusy(true);

            const result = await dispatch(checkSerialNo({
                computerizedSystemId: computerizedSystem.id,
                computerizedSystemIdentifier: normalizedIdentifier,
                structureId: normalizedStructureId,
                supplierId: normalizedSupplierId,
            }));

            if (identifierValidationRequestRef.current !== requestId) {
                return;
            }

            if (checkSerialNo.fulfilled.match(result)) {
                setIdentifierValidation(result.payload as CheckSerialNoResponse);
            } else {
                setIdentifierValidation({ status: "SERVER_ERROR", level: 4 });
            }

            const elapsed = Date.now() - busyShownAt;
            const remaining = 350 - elapsed;

            if (remaining > 0) {
                identifierValidationHideBusyTimeoutRef.current = window.setTimeout(() => {
                    if (identifierValidationRequestRef.current !== requestId) {
                        return;
                    }
                    setIdentifierValidationBusy(false);
                }, remaining);
            } else {
                setIdentifierValidationBusy(false);
            }
        }, 500);

        return () => {
            window.clearTimeout(timeoutId);
            if (identifierValidationHideBusyTimeoutRef.current !== null) {
                window.clearTimeout(identifierValidationHideBusyTimeoutRef.current);
                identifierValidationHideBusyTimeoutRef.current = null;
            }
        };
    }, [dispatch, identifier, structureId, computerizedSystem.id, computerizedSystem.supplierId]);

    const handleSave = async () => {
        setValidationErrors(true);
        setServerError(null);
        if (!name.trim() || !structureId || !identifier.trim()) return;

        setSaving(true);
        try {
            const res = await dispatch(
                updateComputerizedSystemBasicInfo({
                    id: computerizedSystem.id,
                    name: name.trim(),
                    computerizedSystemIdentifier: identifier.trim(),
                    description: description?.trim() ? description.trim() : "",
                    structureId,
                    gampCategory: gampCategory === "" ? null : (gampCategory as GampCategory)

                })
            );
            if (updateComputerizedSystemBasicInfo.fulfilled.match(res)) {
                const updated = res.payload as ComputerizedSystem;
                dispatch(showSuccess("saved"))
                onUpdate(updated);
            } else {
                let errorMsg = "error"
                if (res.payload && res.payload.kind === 'http') {
                    const problem = res.payload.problem
                    if (problem) {
                        if(problem.status === 400 && problem.detail) {
                            errorMsg = problem.detail
                        } else {
                            errorMsg = problem.title
                        }

                    }
                }
                setServerError(errorMsg)
            }

        } catch (e: any) {
            dispatch(showError("error"))
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={true} onClose={onCancel} maxWidth="md" fullWidth>
            <DialogTitle sx={{
                bgcolor: (theme) => theme.palette.grey[100],
                borderBottom: 1,
                borderColor: 'divider',
                py: 1.5,
            }}>
                <Localized id="cs-basic-edit">Edytuj podstawowe informacje</Localized>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label={<Localized id="cs-name">Nazwa</Localized>}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth required error={nameErr}
                            helperText={nameErr ? <span>Wymagane</span> : serverError}
                            disabled={saving}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <StructureTreeSelect
                            defaultStructureId={structureId}
                            onStructureSelected={(id) => setStructureId(id)}
                        />
                        {structureErr && <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 4 }}>Wymagane</div>}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={<Localized id="cs-identifier">Numer identyfikacyjny</Localized>}
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            fullWidth required error={identifierErr}
                            helperText={identifierErr ? <span>Wymagane</span> : undefined}
                            disabled={saving}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={<Localized id="cs-description">Opis</Localized>}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            fullWidth multiline minRows={3}
                            disabled={saving}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <GampCategorySelect
                            value={gampCategory}
                            onChange={setGampCategory}
                            error={gampError}
                            helperText={<AppRequiredMessage />}
                        />
                    </Grid>

                </Grid>
                {(identifierValidationBusy || identifierValidation) && (
                    <Box sx={{ mt: 1 }}>
                        {identifierValidationBusy ? (
                            <Alert severity="info" sx={{ m: 0 }}>Sprawdzanie numeru identyfikacyjnego...</Alert>
                        ) : identifierValidation ? (
                            <StatusLevelAlert type="CS" status={identifierValidation.status} level={identifierValidation.level} sx={{ m: 0 }} />
                        ) : null}
                    </Box>
                )}
                {
                    serverError && <Alert sx={{ marginTop: 1, }} onClose={() => { setServerError("") }} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                }
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} onClick={handleSave}></SaveButton>
                <CancelButton loading={saving} onClick={onCancel}></CancelButton>
            </DialogActions>
        </Dialog>
    );
};
